import React, { useState, ChangeEvent, useEffect } from 'react';
import {
  TextField,
  Grid,
  InputBaseComponentProps,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import { makeStyles, createStyles } from '@mui/styles';
import { checkIsCountryCodeValid, checkIsEmailValid, checkIsPhoneNumberValid } from '../util/Validators';
import { Theme } from '@mui/material/styles';
import { PayerPaymentMethod, PaymentMethodType } from '../gql-types.generated';

interface EditCard {
  cardBrand: string;
  lastFour: string;
  expirationMonth: number;
  expirationYear: string;
  cardHolder: {
    holderName: string;
  };
  emailAddress: string;
  phoneNumber: {
    number: string;
    countryCode: string;
  };
  isDefault: boolean;
}

interface PaymentMethodEditDialogProps {
  initialEmail: string;
  initialPhoneNumber: string;
  initialCountryCode: string;
  onEmailChange: (email: string) => void;
  onPhoneNumberChange: (phone: string) => void;
  onCountryCodeChange: (code: string) => void;
  onValidationChange: (isValid: boolean) => void;
  editingMethod: PayerPaymentMethod | null;
  editValue: string;
  setEditValue: (value: string) => void;
  loading: boolean;
  isValid: boolean;
  editCard: EditCard;
  classes: Record<string, string>;
  getPaymentMethodIcon: (
    method: PayerPaymentMethod | undefined,
    isDefault: boolean,
    size: number,
    showCardIcon: boolean,
  ) => React.ReactNode;
  setEditingMethod: (method: PayerPaymentMethod | null) => void;
  setEditCard: (card: EditCard) => void;
  handleUpdatePaymentMethod: () => void;
  errorMessage: string | null;
}

interface NumberFormatCustomProps extends Omit<InputBaseComponentProps, 'defaultValue'> {
  inputRef: (instance: NumberFormat | null) => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  defaultValue?: string | number;
}

const PhoneNumberFormat = React.forwardRef<NumberFormat, NumberFormatCustomProps>(function phoneNumberFormat(props, _ref) {
  const { inputRef, onChange, ...other } = props;
  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={(values: NumberFormatValues) => {
        onChange({
          target: {
            value: values.value,
          },
        } as React.ChangeEvent<HTMLInputElement>);
      }}
      format="(###) ###-####"
    />
  );
});

PhoneNumberFormat.displayName = 'PhoneNumberFormat';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    countryCodeContainer: {
      marginTop: '2px',
      marginLeft: '1px',
    },
    textField: {
      width: '100%',
    },
    cardDetailsContainer: {
      marginBottom: '5px',
    },
    checkboxContainer: {
      marginTop: '10px',
    },
    cardDetailsText: {
      marginBottom: theme.spacing(1),
    },
    cardspace: {
      paddingBottom: '5px',
    },
    checkboxLabel: {
      marginLeft: theme.spacing(1),
    },
    cardDetailsIcon: {
      marginLeft: 'auto',
    },
    saveButton: {
      backgroundColor: theme.palette.primary.main,
      color: 'white',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
      '&.Mui-disabled': {
        backgroundColor: '#D3D3D3',
        color: '#A9A9A9',
      },
    },
  }),
);

const PaymentMethodEditDialog: React.FC<PaymentMethodEditDialogProps> = ({
  initialEmail,
  initialPhoneNumber,
  initialCountryCode,
  onEmailChange,
  onPhoneNumberChange,
  onCountryCodeChange,
  onValidationChange,
  editingMethod,
  editCard,
  editValue,
  setEditValue,
  loading,
  handleUpdatePaymentMethod,
  getPaymentMethodIcon,
  isValid,
  setEditingMethod,
  setEditCard,
  errorMessage,
}) => {
  const [customerEmail, setCustomerEmail] = useState(initialEmail);
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [countryCode, setCountryCode] = useState(initialCountryCode);
  const [isCustomerEmailValid, setCustomerEmailValid] = useState(true);
  const [isCustomerPhoneValid, setCustomerPhoneValid] = useState(true);
  const [isCustomerCountryCodeValid, setCustomerCountryCodeValid] = useState(true);
  const classes = useStyles();

  const handleCustomerEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setCustomerEmail(e.target.value);
    setCustomerEmailValid(checkIsEmailValid(email));
    onEmailChange(e.target.value);
  };

  const validateEmail = () => {
    setCustomerEmailValid(checkIsEmailValid(customerEmail));
  };

  const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    setPhoneNumber(e.target.value);
    setCustomerPhoneValid(checkIsPhoneNumberValid(phone));
    onPhoneNumberChange(e.target.value);
  };

  const validatePhoneNumber = () => {
    setCustomerPhoneValid(checkIsPhoneNumberValid(phoneNumber));
  };

  const handleCountryCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const code = e.target.value;
    setCountryCode(e.target.value);
    setCustomerCountryCodeValid(checkIsCountryCodeValid(code));
    onCountryCodeChange(e.target.value);
  };

  const validateCountryCode = () => {
    setCustomerCountryCodeValid(checkIsCountryCodeValid(countryCode));
  };

  useEffect(() => {
    setCustomerEmail(initialEmail);
    setPhoneNumber(initialPhoneNumber);
    setCountryCode(initialCountryCode);
  }, [initialEmail, initialPhoneNumber, initialCountryCode]);

  useEffect(() => {
    const isFormValid =
      isCustomerEmailValid &&
      isCustomerPhoneValid &&
      isCustomerCountryCodeValid &&
      customerEmail !== '' &&
      phoneNumber !== '' &&
      countryCode !== '';
    onValidationChange(isFormValid);
  }, [
    isCustomerEmailValid,
    isCustomerPhoneValid,
    isCustomerCountryCodeValid,
    customerEmail,
    phoneNumber,
    countryCode,
    onValidationChange,
  ]);

  const isCreditCard = editingMethod?.paymentMethod?.type === PaymentMethodType.CreditCard;
  const isBankAccount = editingMethod?.paymentMethod?.type === PaymentMethodType.PaymentBankUs;

  const renderCardDetails = (title: string, showBrand: boolean, showCardIcon: boolean) => {
    let paymentMethodIcon;
    try {
      paymentMethodIcon = getPaymentMethodIcon(editingMethod ?? undefined, false, 62, showCardIcon);
    } catch (error) {
      console.error('Error rendering payment method icon:', error);
      paymentMethodIcon = <Typography variant="subtitle2">Icon unavailable</Typography>;
    }

    return (
      <Grid container className={classes.cardDetailsContainer}>
        <Grid item>
          {showBrand && <Typography variant="subtitle1">{editCard.cardBrand}</Typography>}
          <Typography variant="subtitle2">**** **** **** {editCard.lastFour}</Typography>
          <Typography variant="subtitle2" className={classes.cardDetailsText}>
            {editCard.cardHolder.holderName}
          </Typography>
        </Grid>
        <Grid item className={classes.cardDetailsIcon}>
          {paymentMethodIcon}
        </Grid>
      </Grid>
    );
  };

  const renderDefaultCheckbox = (id: string) =>
    !editingMethod?.isDefault && (
      <Grid container className={classes.checkboxContainer}>
        <Grid item>
          <input type="checkbox" id={id} onChange={e => setEditCard({ ...editCard, isDefault: e.target.checked })} />
        </Grid>
        <Grid item>
          <label htmlFor={id} className={classes.checkboxLabel}>
            Make this my default payment method
          </label>
        </Grid>
      </Grid>
    );

  return (
    <Dialog open onClose={() => setEditingMethod(null)}>
      <DialogTitle className={classes.cardspace}>Edit Payment Method</DialogTitle>
      <DialogContent>
        {isCreditCard && renderCardDetails('', true, true)}
        {isBankAccount && renderCardDetails('Bank Account', false, false)}
        {!isCreditCard && !isBankAccount && (
          <TextField label="Payment Method Details" value={editValue} onChange={e => setEditValue(e.target.value)} fullWidth />
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              id="email-address-id"
              fullWidth
              variant="outlined"
              label="Email Address"
              autoComplete="off"
              type="email"
              aria-label="Email Address"
              value={customerEmail}
              onChange={handleCustomerEmailChange}
              onBlur={validateEmail}
              error={!isCustomerEmailValid}
              helperText={!isCustomerEmailValid ? 'Invalid Email Address' : ''}
              InputProps={{
                'aria-describedby': isCustomerEmailValid ? undefined : 'email-address-helpertext-id',
              }}
              FormHelperTextProps={{ id: 'email-address-helpertext-id' }}
            />
          </Grid>
          <Grid container spacing={2} alignItems="flex-start" className={classes.countryCodeContainer}>
            <Grid item xs={3}>
              <TextField
                id="country-code-id"
                fullWidth
                variant="outlined"
                label="Country Code"
                autoComplete="off"
                aria-label="Country Code"
                value={countryCode}
                onChange={handleCountryCodeChange}
                onBlur={validateCountryCode}
                error={!isCustomerCountryCodeValid}
                helperText={!isCustomerCountryCodeValid ? 'Invalid Country Code' : ''}
                InputProps={{
                  'aria-describedby': isCustomerCountryCodeValid ? undefined : 'country-code-helpertext-id',
                }}
                FormHelperTextProps={{ id: 'country-code-helpertext-id' }}
              />
            </Grid>
            <Grid item xs={9}>
              <TextField
                id="phone-number-id"
                fullWidth
                variant="outlined"
                label="Phone Number"
                aria-label="Phone Number"
                autoComplete="off"
                type="tel"
                value={phoneNumber}
                onChange={handlePhoneNumberChange}
                onBlur={validatePhoneNumber}
                error={!isCustomerPhoneValid}
                helperText={!isCustomerPhoneValid ? 'Invalid Phone Number' : ''}
                InputProps={{
                  inputComponent: PhoneNumberFormat,
                  'aria-describedby': isCustomerPhoneValid ? undefined : 'phone-number-helpertext-id',
                }}
                InputLabelProps={phoneNumber ? { shrink: true } : {}}
              />
            </Grid>
          </Grid>
          <Grid item xs={12}>
            {renderDefaultCheckbox('make-default')}
          </Grid>
        </Grid>
        {errorMessage && (
          <Typography color="error" variant="body2">
            {errorMessage}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditingMethod(null)} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleUpdatePaymentMethod} disabled={loading || !isValid} className={classes.saveButton}>
          {loading ? <CircularProgress size={24} color={loading || !isValid ? 'inherit' : 'primary'} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default PaymentMethodEditDialog;
